import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { LoginResponse, ApiResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL + '/api' || 'http://localhost:5010/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Attach active organization context (numeric org IDs only;
        // admin pseudo-ids like 'user-management' are skipped)
        const selectedOrgStr = localStorage.getItem('selectedOrg');
        if (selectedOrgStr && config.headers) {
          try {
            const selectedOrg = JSON.parse(selectedOrgStr);
            const orgId = Number(selectedOrg?.id);
            if (Number.isInteger(orgId) && orgId > 0) {
              config.headers['x-organization-id'] = String(orgId);
            }
          } catch {
            // Malformed stored org - skip header
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/login', { username, password });
    return response.data;
  }

  // Scorecard
  async getPersonalScorecard(selectedDay?: string, period: string = 'currentWeek') {
    // Get user from localStorage to extract userId
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userId = user?.id || user?.userId;
    
    if (!userId) {
      throw new Error('User ID not found. Please log in again.');
    }
    
    let url = `/scorecard/personal?userId=${userId}&period=${period}`;
    if (selectedDay) {
      url += `&day=${selectedDay}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async getTeamScorecard(period: string = 'currentWeek') {
    const response = await this.api.get(`/scorecard/team?period=${period}`);
    return response.data;
  }

  // Sales
  async getSalesData() {
    const response = await this.api.get('/sales/data');
    return response.data;
  }

  async getMySales(filter: string = 'all') {
    const response = await this.api.get(`/my-sales?filter=${filter}`);
    return response.data;
  }

  async createMySale(saleData: any) {
    const response = await this.api.post('/my-sales', saleData);
    return response.data;
  }

  // Embed Sales (Sales_Tracker_EmbedCare - 1776 Rx)
  async getEmbedSalesProducts() {
    const response = await this.api.get('/embed-sales/products');
    return response.data;
  }

  async getEmbedSales(filter: string = 'week', startDate?: string, endDate?: string, viewAll: boolean = false) {
    const params: any = { filter };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (viewAll) params.all = 'true';
    const response = await this.api.get('/embed-sales', { params });
    return response.data;
  }

  async getEmbedSalesMtdSummary() {
    const response = await this.api.get('/embed-sales/mtd-summary');
    return response.data;
  }

  async createEmbedSale(saleData: { customerId: string; product: string; saleAmount: number; saleType: string }) {
    const response = await this.api.post('/embed-sales', saleData);
    return response.data;
  }

  async updateEmbedSale(id: number, saleData: { customerId?: string; product?: string; saleAmount?: number; saleType?: string }) {
    const response = await this.api.put(`/embed-sales/${id}`, saleData);
    return response.data;
  }

  async deleteEmbedSale(id: number) {
    const response = await this.api.delete(`/embed-sales/${id}`);
    return response.data;
  }

  // Commission
  async calculateCommissions(period: string = 'currentWeek', startDate?: string, endDate?: string) {
    let url = `/commission/calculate?period=${period}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async getCommissionData(period: string = 'currentWeek', startDate?: string | null, endDate?: string | null) {
    let url = `/commission/calculate?period=${period}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async exportWeeklyInvoicing(period: string = 'currentWeek', startDate?: string | null, endDate?: string | null): Promise<Blob> {
    let url = `/commission/export-weekly-invoicing?period=${period}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    const response = await this.api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Timeclock
  async clockIn(organizationId?: number, notes?: string) {
    // organizationId is now handled automatically by backend from authenticated user.
    // Send the punching computer's timestamp and IANA timezone so the punch is
    // anchored to the exact instant AND the place it happened.
    const response = await this.api.post('/timeclock/clock-in', {
      notes,
      clockInTime: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return response.data;
  }

  async clockOut() {
    const response = await this.api.post('/timeclock/clock-out', {
      clockOutTime: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return response.data;
  }

  async getActiveTimeEntry() {
    const response = await this.api.get('/timeclock/active');
    return response.data;
  }

  async getCurrentWeekEntries() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.api.get(`/timeclock/current-week?tz=${encodeURIComponent(tz)}`);
    return response.data;
  }

  // User Management
  async getUsers(organizationId?: number) {
    const url = organizationId 
      ? `/user-management/users?organizationId=${organizationId}`
      : '/user-management/users';
    const response = await this.api.get(url);
    return response.data;
  }

  async createUser(userData: any) {
    const response = await this.api.post('/user-management/users', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: any) {
    const response = await this.api.put(`/user-management/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number) {
    const response = await this.api.delete(`/user-management/users/${userId}`);
    return response.data;
  }

  // Organizations
  async getOrganizations() {
    const response = await this.api.get('/organizations');
    return response.data;
  }

  async createOrganization(orgData: any) {
    const response = await this.api.post('/organizations', orgData);
    return response.data;
  }

  async updateOrganization(id: number, orgData: any) {
    const response = await this.api.put(`/organizations/${id}`, orgData);
    return response.data;
  }

  async deleteOrganization(id: number) {
    const response = await this.api.delete(`/organizations/${id}`);
    return response.data;
  }

  async getOrganizationModules(orgId: number) {
    const response = await this.api.get(`/organizations/${orgId}/modules`);
    return response.data;
  }

  async updateOrganizationModules(orgId: number, moduleIds: number[]) {
    const response = await this.api.put(`/organizations/${orgId}/modules`, {
      module_ids: moduleIds,
    });
    return response.data;
  }

  // Organization Integrations
  async getOrganizationIntegrations(orgId: number) {
    const response = await this.api.get(`/organizations/${orgId}/integrations`);
    return response.data;
  }

  async upsertOrganizationIntegration(orgId: number, data: any) {
    const response = await this.api.put(`/organizations/${orgId}/integrations`, data);
    return response.data;
  }

  // Embed Call (call center phone reporting)
  async getEmbedPhoneStatus() {
    const response = await this.api.get('/embed-phone/status');
    return response.data;
  }

  async getEmbedPhoneTargets() {
    const response = await this.api.get('/embed-phone/targets');
    return response.data;
  }

  async getEmbedPhoneCalls(startedAfter?: number, startedBefore?: number, cursor?: string) {
    const params: any = {};
    if (startedAfter) params.startedAfter = startedAfter;
    if (startedBefore) params.startedBefore = startedBefore;
    if (cursor) params.cursor = cursor;
    const response = await this.api.get('/embed-phone/calls', { params });
    return response.data;
  }

  async getEmbedPhoneReport(start: number, end: number, timezone?: string, refresh: boolean = false) {
    const params: any = { start, end };
    if (timezone) params.timezone = timezone;
    if (refresh) params.refresh = 'true';
    const response = await this.api.get('/embed-phone/report', { params });
    return response.data;
  }

  async getEmbedPhoneStats(daysAgoStart: number, daysAgoEnd: number, statType: string = 'calls') {
    const response = await this.api.post('/embed-phone/stats', {
      daysAgoStart,
      daysAgoEnd,
      statType,
    });
    return response.data;
  }

  // CRM
  async getCrmLeads(filters?: any) {
    const params = new URLSearchParams(filters).toString();
    const response = await this.api.get(`/crm/leads?${params}`);
    return response.data;
  }

  async createCrmLead(leadData: any) {
    const response = await this.api.post('/crm/leads', leadData);
    return response.data;
  }

  async updateCrmLead(leadId: number, leadData: any) {
    const response = await this.api.put(`/crm/leads/${leadId}`, leadData);
    return response.data;
  }

  async deleteCrmLead(leadId: number) {
    const response = await this.api.delete(`/crm/leads/${leadId}`);
    return response.data;
  }

  // Dynamic Trends
  async getTrendsData(userId?: number) {
    const url = userId ? `/dynamic-trends/data?userId=${userId}` : '/dynamic-trends/data';
    const response = await this.api.get(url);
    return response.data;
  }

  // Weekly Planning
  async getWeekStart() {
    const response = await this.api.get('/weekly-planning/week-start');
    return response.data;
  }

  async getCurrentWeeklyPlan() {
    const response = await this.api.get('/weekly-planning/current');
    return response.data;
  }

  async getTeamWeeklyPlans(organizationId: number) {
    const response = await this.api.get('/weekly-planning/team/current', {
      params: { organizationId }
    });
    return response.data;
  }

  async submitWeeklyPlan(planData: any) {
    const response = await this.api.post('/weekly-planning/submit', planData);
    return response.data;
  }

  // Commission - Personal
  async getPersonalCommission(period: string = 'currentWeek', userId?: number) {
    const url = userId 
      ? `/commission/personal?userId=${userId}&period=${period}`
      : `/commission/personal?period=${period}`;
    const response = await this.api.get(url);
    return response.data;
  }

  // Sales Metrics
  async getSalesMetrics() {
    const response = await this.api.get('/sales/metrics');
    return response.data;
  }

  async getTeamEfficiencyMetrics() {
    const response = await this.api.get('/sales/team-efficiency/metrics');
    return response.data;
  }

  // Timeclock Admin methods
  async getUserTimeEntries(userId: number, startDate: string, endDate: string) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.api.get(`/timeclock/user/${userId}?startDate=${startDate}&endDate=${endDate}&tz=${encodeURIComponent(tz)}`);
    return response.data;
  }

  async updateTimeEntry(entryId: number, data: any) {
    const response = await this.api.put(`/timeclock/entry/${entryId}`, data);
    return response.data;
  }

  async deleteTimeEntry(entryId: number, reason: string) {
    const response = await this.api.delete(`/timeclock/entry/${entryId}`, { data: { deleteReason: reason } });
    return response.data;
  }

  async createManualTimeEntry(data: any) {
    const response = await this.api.post('/timeclock/manual-entry', data);
    return response.data;
  }

  async getTimeEntryHistory(entryId: number) {
    const response = await this.api.get(`/timeclock/entry/${entryId}/history`);
    return response.data;
  }

  async getAllTimeEntries(startDate: string, endDate: string, organizationId: number) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.api.get(`/timeclock/entries?startDate=${startDate}&endDate=${endDate}&organizationId=${organizationId}&tz=${encodeURIComponent(tz)}`);
    return response.data;
  }

  // Sales Validation
  async getValidatedSales() {
    const response = await this.api.get('/sales/validated');
    return response.data;
  }

  // Dynamic Trends
  async getDynamicTrendsData(userId?: number) {
    const url = userId ? `/dynamic-trends/data?userId=${userId}` : '/dynamic-trends/data';
    const response = await this.api.get(url);
    return response.data;
  }

  async getActiveUsersForTrends() {
    const response = await this.api.get('/dynamic-trends/active-users');
    return response.data;
  }

  async getAICoachingInsights(userId?: number) {
    const url = userId ? `/dynamic-trends/ai-coaching?userId=${userId}` : '/dynamic-trends/ai-coaching';
    const response = await this.api.post(url, {}, {
      timeout: 120000 // 2 minutes for AI analysis
    });
    return response.data;
  }

  async getTeamAnalysis() {
    const response = await this.api.post('/dynamic-trends/analyze', {}, {
      timeout: 180000 // 3 minutes for AI analysis
    });
    return response.data;
  }

  async getIndividualAnalysis(agentId: number) {
    const response = await this.api.post('/dynamic-trends/analyze-individual', { agentId }, {
      timeout: 180000
    });
    return response.data;
  }

  async saveCoachingInsight(data: {
    userId?: number;
    isTeamAnalysis: boolean;
    agentName: string;
    insightsText: string;
    weekData: any;
    analysisPeriod: string;
  }) {
    const response = await this.api.post('/dynamic-trends/save-coaching', data);
    return response.data;
  }

  async exportCoachingToPDF(insightId: number) {
    const response = await this.api.get(`/dynamic-trends/coaching/${insightId}/export-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Team Comparison
  async getTeamComparison(dateFilter: string, teams: string[], startDate?: string, endDate?: string) {
    try {
      console.log('🟢 FRONTEND CALLING:', `/team-comparison/data?dateFilter=${dateFilter}&teams=${teams.join(',')}`);
      const params = new URLSearchParams({ dateFilter });
      if (dateFilter === 'dateRange' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      params.append('teams', teams.join(','));
      const response = await this.api.get(`/team-comparison/data?${params}`);
      console.log('🟢 FRONTEND RECEIVED:', response.data);
      return response.data.data || response.data; // Handle both { data: {...} } and flat response
    } catch (error) {
      console.error('🔴 FRONTEND API ERROR:', error);
      throw error;
    }
  }

  // Sales Audit
  async runSalesAudit() {
    const response = await this.api.post('/sales-audit/run-audit', {});
    return response.data;
  }

  async updateSalesEmail(rowIndex: number, correctEmail: string) {
    const response = await this.api.post('/sales-audit/update-email', { rowIndex, correctEmail });
    return response.data;
  }

  async bulkAddSales(sales: any[]) {
    const response = await this.api.post('/sales-audit/bulk-add', { sales });
    return response.data;
  }

  async runAutomatedAudit() {
    const response = await this.api.post('/sales-audit/run-pipeline', {});
    return response.data;
  }

  // Previous Week Coaching
  async getPreviousWeekCoaching() {
    const response = await this.api.get('/previous-week-coaching/data');
    return response.data;
  }

  // Enhanced Agent Efficiency - Get all teams efficiency data
  getAllTeamsEfficiencyData = async (): Promise<any> => {
    try {
      console.log('🔵 [API] Calling /sales/all-teams-efficiency/data');
      const response = await this.api.get('/sales/all-teams-efficiency/data');
      console.log('🟢 [API] Response received:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🔴 [API] Error in getAllTeamsEfficiencyData:', error.response?.data || error.message);
      throw error;
    }
  }

  // Agent Efficiency - Get SoniqCX agent efficiency data
  getAgentEfficiencyData = async (): Promise<any> => {
    try {
      console.log('🔵 [API] Calling /sales/agent-efficiency/data');
      const response = await this.api.get('/sales/agent-efficiency/data');
      console.log('🟢 [API] Response received:', response.data);
      return response.data; // Returns { success: true, data: {...} }
    } catch (error: any) {
      console.error('🔴 [API] Error in getAgentEfficiencyData:', error.response?.data || error.message);
      throw error;
    }
  }

  // Agent Efficiency - Get SoniqCX agent efficiency metrics
  getAgentEfficiencyMetrics = async (): Promise<any> => {
    try {
      console.log('🔵 [API] Calling /sales/agent-efficiency/metrics');
      const response = await this.api.get('/sales/agent-efficiency/metrics');
      console.log('🟢 [API] Response received:', response.data);
      return response.data; // Returns { success: true, metrics: {...} }
    } catch (error: any) {
      console.error('🔴 [API] Error in getAgentEfficiencyMetrics:', error.response?.data || error.message);
      throw error;
    }
  }

  // Call Analysis - Upload and analyze call recording
  async uploadCallRecording(file: File, metadata: {
    agentId: number;
    organizationId: number;
    callDate?: string;
    customerType?: string;
    callOutcome?: string;
    notes?: string;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('agentId', metadata.agentId.toString());
    formData.append('organizationId', metadata.organizationId.toString());
    if (metadata.callDate) formData.append('callDate', metadata.callDate);
    if (metadata.customerType) formData.append('customerType', metadata.customerType);
    if (metadata.callOutcome) formData.append('callOutcome', metadata.callOutcome);
    if (metadata.notes) formData.append('notes', metadata.notes);

    const response = await this.api.post('/call-analysis/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Call Analysis - Get all analyses for organization
  async getAllCallAnalyses(): Promise<any> {
    const response = await this.api.get('/call-analysis');
    return response.data;
  }

  // Call Analysis - Get single analysis
  async getCallAnalysis(id: number): Promise<any> {
    const response = await this.api.get(`/call-analysis/${id}`);
    return response.data;
  }

  // Call Analysis - Delete analysis
  async deleteCallAnalysis(id: number): Promise<any> {
    const response = await this.api.delete(`/call-analysis/${id}`);
    return response.data;
  }

  // ========================================
  // MODULE PERMISSIONS
  // ========================================

  // Get user's allowed modules
  async getUserAllowedModules(): Promise<any[]> {
    const response = await this.api.get('/module-permissions/user-modules');
    return response.data;
  }

  // Permission Groups
  async getAllPermissionGroups(): Promise<any[]> {
    const response = await this.api.get('/module-permissions/groups');
    return response.data;
  }

  async getPermissionGroupById(id: number): Promise<any> {
    const response = await this.api.get(`/module-permissions/groups/${id}`);
    return response.data;
  }

  async createPermissionGroup(data: any): Promise<any> {
    const response = await this.api.post('/module-permissions/groups', data);
    return response.data;
  }

  async updatePermissionGroup(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/module-permissions/groups/${id}`, data);
    return response.data;
  }

  async deletePermissionGroup(id: number): Promise<any> {
    const response = await this.api.delete(`/module-permissions/groups/${id}`);
    return response.data;
  }

  // Modules
  async getAllModules(categoryId?: number): Promise<any[]> {
    const params = categoryId ? { category_id: categoryId } : {};
    const response = await this.api.get('/module-permissions/modules', { params });
    return response.data;
  }

  async getModuleById(id: number): Promise<any> {
    const response = await this.api.get(`/module-permissions/modules/${id}`);
    return response.data;
  }

  async createModule(data: any): Promise<any> {
    const response = await this.api.post('/module-permissions/modules', data);
    return response.data;
  }

  async updateModule(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/module-permissions/modules/${id}`, data);
    return response.data;
  }

  async deleteModule(id: number): Promise<any> {
    const response = await this.api.delete(`/module-permissions/modules/${id}`);
    return response.data;
  }

  // Module Assignments
  async getModulesForGroup(groupId: number): Promise<any[]> {
    const response = await this.api.get(`/module-permissions/groups/${groupId}/modules`);
    return response.data;
  }

  async assignModulesToGroup(groupId: number, moduleIds: number[]): Promise<any> {
    const response = await this.api.post(`/module-permissions/groups/${groupId}/modules`, {
      module_ids: moduleIds,
    });
    return response.data;
  }

  async removeModuleFromGroup(groupId: number, moduleId: number): Promise<any> {
    const response = await this.api.delete(
      `/module-permissions/groups/${groupId}/modules/${moduleId}`
    );
    return response.data;
  }

  // Categories
  async getAllCategories(): Promise<any[]> {
    const response = await this.api.get('/module-permissions/categories');
    return response.data;
  }

  async getCategoryById(id: number): Promise<any> {
    const response = await this.api.get(`/module-permissions/categories/${id}`);
    return response.data;
  }

  async createCategory(data: any): Promise<any> {
    const response = await this.api.post('/module-permissions/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/module-permissions/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<any> {
    const response = await this.api.delete(`/module-permissions/categories/${id}`);
    return response.data;
  }

  // ========================================
  // SPIFF MANAGEMENT
  // ========================================

  // Get all SPIFFs
  async getAllSpiffs(): Promise<any[]> {
    const response = await this.api.get('/spiff');
    return response.data;
  }

  // Get SPIFFs for a specific user
  async getUserSpiffs(userId: number): Promise<any[]> {
    const response = await this.api.get(`/spiff/user/${userId}`);
    return response.data;
  }

  // Get total SPIFF amount for a user
  async getUserSpiffTotal(userId: number): Promise<number> {
    const response = await this.api.get(`/spiff/user/${userId}/total`);
    return response.data.total;
  }

  // Get active users for SPIFF assignment
  async getActiveUsersForSpiff(): Promise<any[]> {
    const response = await this.api.get('/spiff/active-users');
    return response.data;
  }

  // Create a new SPIFF
  async createSpiff(data: { user_id: number; description: string; amount: number; spiff_date?: string }): Promise<any> {
    const response = await this.api.post('/spiff', data);
    return response.data;
  }

  // Update a SPIFF
  async updateSpiff(id: number, data: { description: string; amount: number; spiff_date?: string }): Promise<any> {
    const response = await this.api.patch(`/spiff/${id}`, data);
    return response.data;
  }

  // Delete a SPIFF
  async deleteSpiff(id: number): Promise<any> {
    const response = await this.api.delete(`/spiff/${id}`);
    return response.data;
  }

  // Get SPIFFs by date range
  async getSpiffsByDateRange(startDate: string, endDate: string): Promise<any> {
    const response = await this.api.get('/spiff/date-range', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  // ====== COACHING INSIGHTS ======

  // Get coaching history (optionally filtered by userId)
  async getCoachingHistory(userId?: number): Promise<any> {
    const params = userId ? { userId } : {};
    const response = await this.api.get('/dynamic-trends/coaching-history', { params });
    return response.data;
  }
}

export default new ApiService();
